/**
 * @fileoverview Blocking modal that requires users to re-accept updated Terms of Service.
 *
 * Renders when AuthContext.requiresTermsAcceptance is true, which is set by
 * /api/auth/me when the user's most recent consent_records.terms_version is
 * older than the current system_settings.terms_version.
 *
 * This is a BLOCKING gate — no close button, no backdrop dismiss. The user
 * cannot navigate anywhere in the app until they click Accept.
 *
 * On acceptance:
 *   1. Calls AuthContext.acceptTerms() which POSTs to /api/auth/accept-terms
 *   2. acceptTerms() re-fetches /api/auth/me to clear requiresTermsAcceptance
 *   3. Modal self-dismisses when requiresTermsAcceptance becomes false
 *
 * LEGAL-06: Re-consent record written to consent_records on accept.
 */

import React, { useState, useEffect, useRef, useId } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './TermsReacceptanceModal.css';

/**
 * Blocking modal that gates access until the user accepts updated terms.
 *
 * Self-manages visibility via AuthContext — returns null when not needed.
 *
 * @returns {JSX.Element|null} Modal overlay or null if not required
 */
function TermsReacceptanceModal() {
  const { requiresTermsAcceptance, currentTermsVersion, acceptTerms } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const headingId = useId();
  const acceptButtonRef = useRef(null);

  // Focus the accept button when the modal becomes visible
  useEffect(() => {
    if (requiresTermsAcceptance && acceptButtonRef.current) {
      acceptButtonRef.current.focus();
    }
  }, [requiresTermsAcceptance]);

  // Don't render when not needed — this keeps the component lightweight
  if (!requiresTermsAcceptance) {
    return null;
  }

  /**
   * Handle acceptance of updated terms.
   *
   * Calls AuthContext.acceptTerms() which writes a new consent_record via
   * POST /api/auth/accept-terms, then re-fetches /api/auth/me so this modal
   * self-dismisses when requiresTermsAcceptance becomes false.
   */
  async function handleAccept() {
    setLoading(true);
    setError(null);
    try {
      await acceptTerms();
    } catch (err) {
      setError('Failed to record acceptance. Please try again.');
      setLoading(false);
    }
    // Don't setLoading(false) on success — modal will unmount when
    // requiresTermsAcceptance clears, so the button stays disabled until gone.
  }

  return (
    <div className="terms-modal-overlay" aria-hidden={false}>
      <div
        className="terms-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
      >
        <h2
          id={headingId}
          className="terms-modal-heading"
        >
          Terms of Service Updated
        </h2>

        <p className="terms-modal-body">
          Our Terms of Service have been updated
          {currentTermsVersion && (
            <> (effective <strong>{currentTermsVersion}</strong>)</>
          )}
          . Please review and accept the updated terms to continue using COGSLOG.
        </p>

        <p className="terms-modal-links">
          Review our{' '}
          <a
            href="https://cogslog.com/legal/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="terms-modal-link"
          >
            Terms of Service
          </a>
          {' '}and{' '}
          <a
            href="https://cogslog.com/legal/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="terms-modal-link"
          >
            Privacy Policy
          </a>
          .
        </p>

        {error && (
          <p
            className="terms-modal-error"
            role="status"
            aria-live="polite"
          >
            {error}
          </p>
        )}

        <button
          ref={acceptButtonRef}
          type="button"
          className={`btn btn-primary terms-modal-accept${loading ? ' btn-loading' : ''}`}
          onClick={handleAccept}
          disabled={loading}
          aria-disabled={loading}
        >
          {loading ? 'Accepting…' : 'I Accept the Updated Terms'}
        </button>
      </div>
    </div>
  );
}

export default TermsReacceptanceModal;
